import { ShelterRecordLinkoutOptions } from './shelter-record-linkout.model';

export function buildShelterRecordLinkoutUrl(
  options: ShelterRecordLinkoutOptions,
): string | null {
  if (options.linkoutEnabled === false) {
    return null;
  }

  const software = normalizeValue(options.shelterManagementSoftware)
    .replace(/[\s+\_-]+/g, '')
    .toLowerCase();

  switch (software) {
    case 'shelterluv':
      return buildShelterluvUrl(options);

    case 'shelterbuddy':
      return buildShelterBuddyUrl(options);

    case 'petpoint':
      return buildPetPointUrl(options);

    case 'petpointv2':
      return buildPetPointUrl(options, {
        stripIdPrefix: true,
      });

    /*
     * Chameleon and unsupported software receive
     * no linkout. The consuming component can still
     * display and copy the ID.
     */
    default:
      return null;
  }
}

export function isShelterluvManagementSoftware(
  shelterManagementSoftware: string | null | undefined,
): boolean {
  const software = normalizeValue(shelterManagementSoftware)
    .replace(/[\s+\_-]+/g, '')
    .toLowerCase();

  return software === 'shelterluv';
}

function buildShelterluvUrl(
  options: ShelterRecordLinkoutOptions,
): string | null {
  const shortName = normalizeValue(options.shortName);
  const sourceId = normalizeValue(options.shelterSourceId);

  if (!shortName || !sourceId) {
    return null;
  }

  const recordMarker = options.recordType === 'animal' ? 'A' : 'P';

  return (
    `https://new.shelterluv.com/` +
    `${options.recordType}/` +
    `${encodeURIComponent(shortName)}` +
    `-${recordMarker}-` +
    `${encodeURIComponent(sourceId)}`
  );
}

function buildShelterBuddyUrl(
  options: ShelterRecordLinkoutOptions,
): string | null {
  const shelterDomain = normalizeValue(options.shelterBuddyUrl);
  const sourceId = normalizeValue(options.shelterSourceId);

  if (
    !shelterDomain ||
    !sourceId ||
    !isValidShelterBuddyDomain(shelterDomain)
  ) {
    return null;
  }

  const baseUrl = `https://${shelterDomain}.shelterbuddy.com`;
  const encodedId = encodeURIComponent(sourceId);

  if (options.recordType === 'person') {
    return `${baseUrl}/personinfo/persondetails.aspx?personid=${encodedId}`;
  }

  return `${baseUrl}/animal/search.asp?task=view&animalid=${encodedId}`;
}

function buildPetPointUrl(
  options: ShelterRecordLinkoutOptions,
  config: {
    stripIdPrefix?: boolean;
  } = {},
): string | null {
  let sourceId = normalizeValue(options.shelterSourceId);

  if (!sourceId) {
    return null;
  }

  if (config.stripIdPrefix) {
    const prefixPattern = options.recordType === 'animal' ? /^A00/i : /^P00/i;

    sourceId = sourceId.replace(prefixPattern, '');
  }

  if (!sourceId) {
    return null;
  }

  return (
    `https://sms.petpoint.com/` +
    `sms3/enhanced/` +
    `${options.recordType}/` +
    `${encodeURIComponent(sourceId)}`
  );
}

function isValidShelterBuddyDomain(value: string): boolean {
  /*
   * shelterBuddyUrl contains only the subdomain
   * portion, not a full URL.
   */
  return /^[a-z0-9-]+(?:\.[a-z0-9-]+)*$/i.test(value);
}

function normalizeValue(value: string | null | undefined): string {
  return value?.trim() ?? '';
}
